import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiClient } from '@/lib/api-client'
import logo from '@/assets/logo.svg'

function fuzzyMatch(input: string, target: string): boolean {
  const normalize = (s: string) => s.toLowerCase().trim().replace(/\s+/g, ' ')
  const a = normalize(input)
  const b = normalize(target)
  if (!a || !b) return false
  if (a.length < Math.max(3, Math.ceil(b.length * 0.6))) return false
  if (a === b) return true
  if (b.includes(a) || a.includes(b)) return true

  // Levenshtein distance relative to target length
  const maxDist = Math.max(1, Math.floor(b.length * 0.3))
  const dist = levenshtein(a, b)
  return dist <= maxDist
}

function levenshtein(a: string, b: string): number {
  const m = a.length
  const n = b.length
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))
  for (let i = 0; i <= m; i++) dp[i][0] = i
  for (let j = 0; j <= n; j++) dp[0][j] = j
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1])
    }
  }
  return dp[m][n]
}

export function MerchantRegisterPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const referralCode = searchParams.get('ref') || ''
  const businessId = searchParams.get('business') || ''

  const [expectedBusinessName, setExpectedBusinessName] = useState<string | null>(null)
  const [loadingBusiness, setLoadingBusiness] = useState(false)

  const [form, setForm] = useState({
    merchantName: '',
    merchantEmail: '',
    businessName: '',
    phone: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!businessId || businessId === 'SELF_REGISTER') return
    setLoadingBusiness(true)
    apiClient.get(`/referrals/business/${businessId}`)
      .then((res: any) => setExpectedBusinessName(res.name))
      .catch(() => setExpectedBusinessName(null))
      .finally(() => setLoadingBusiness(false))
  }, [businessId])

  const businessNameMatches = useMemo(() => {
    if (!expectedBusinessName) return true
    if (!form.businessName.trim()) return false
    return fuzzyMatch(form.businessName, expectedBusinessName)
  }, [form.businessName, expectedBusinessName])

  const canSubmit = !!referralCode && !submitting && businessNameMatches && !!form.merchantName && !!form.merchantEmail

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setError('')

    try {
      await apiClient.post('/referrals/register', {
        referralCode,
        merchantEmail: form.merchantEmail,
        merchantName: form.merchantName,
        businessName: form.businessName,
        phone: form.phone,
        businessId: businessId || 'SELF_REGISTER',
        source: 'QR_CODE',
      })
      setSuccess(true)
    } catch (err: any) {
      setError(err.message || t('merchant.registrationFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">&#x2713;</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('merchant.successTitle')}
          </h1>
          <p className="text-gray-600">{t('merchant.successMessage')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <a href="https://u-like-it.de" target="_blank" rel="noopener noreferrer">
            <img src={logo} alt="U-Like-It" className="h-12 w-auto mx-auto mb-4" />
          </a>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('merchant.registerTitle')}
          </h1>
          <p className="text-gray-600 mt-2">{t('merchant.registerSubtitle')}</p>
        </div>

        {!referralCode && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            {t('merchant.noReferralCode')}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('merchant.yourName')} *
            </label>
            <input
              type="text"
              required
              value={form.merchantName}
              onChange={(e) => setForm({ ...form, merchantName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('merchant.email')} *
            </label>
            <input
              type="email"
              required
              value={form.merchantEmail}
              onChange={(e) => setForm({ ...form, merchantEmail: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('merchant.businessName')} {expectedBusinessName ? '*' : ''}
            </label>
            <input
              type="text"
              required={!!expectedBusinessName}
              value={form.businessName}
              onChange={(e) => setForm({ ...form, businessName: e.target.value })}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                form.businessName.trim() && expectedBusinessName
                  ? businessNameMatches
                    ? 'border-green-400 bg-green-50'
                    : 'border-red-300 bg-red-50'
                  : 'border-gray-300'
              }`}
              placeholder={loadingBusiness ? t('common.loading') : ''}
            />
            {form.businessName.trim() && expectedBusinessName && !businessNameMatches && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <span className="text-red-500 text-lg leading-none">&#9888;</span>
                <div>
                  <p className="text-sm font-medium text-red-800">
                    {t('merchant.businessNameMismatchTitle')}
                  </p>
                  <p className="text-sm text-red-700 mt-0.5">
                    {t('merchant.businessNameMismatch')}
                  </p>
                </div>
              </div>
            )}
            {form.businessName.trim() && expectedBusinessName && businessNameMatches && (
              <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <span className="text-green-500 text-lg leading-none">&#10003;</span>
                <p className="text-sm font-medium text-green-800">
                  {t('merchant.businessNameMatch')}
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('merchant.phone')}
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {submitting ? t('common.loading') : t('merchant.register')}
          </button>
        </form>

        {referralCode && (
          <div className="mt-4 text-center text-xs text-gray-400">
            {t('merchant.referredBy')}: {referralCode}
          </div>
        )}
      </div>
    </div>
  )
}
